// Core imports
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useParams } from 'react-router-dom';

// Component imports
import { Tabs } from 'antd';
import TestSetEditForm from '../Forms/TestSetEditForm';
import TestSetRunTestForm from '../Forms/TestSetRunTestForm';
import TestSetResultsForm from '../Forms/TestSetResultsForm';
import Permissioned from '../Permissioned';

// Misc imports
import { TEST_SETS_SEARCH_PARAMS, TEST_SET_TABS } from '../../constants/testSets';
import { PERMISSIONS_GROUP_EDITOR } from '../../constants/main';
import { useTestSetNavigation } from '../../utils/hooks/useTestSetsNavigation';
import useUserPermissions from '../../utils/hooks/useUserPermissions';

const TEST_SET_ACTIVE_TABS = {
  TEST_SET_EDIT_TAB: {
    key: TEST_SET_TABS.TEST_SET.key,
    label: TEST_SET_TABS.TEST_SET.label,
    renderIcon: (props) => TEST_SET_TABS.TEST_SET.renderIcon(props),
    children: (props) => <TestSetEditForm {...props} />,
    editOnly: false
  },
  TEST_SET_RUN_TAB: {
    key: TEST_SET_TABS.TEST_RUN.key,
    label: TEST_SET_TABS.TEST_RUN.label,
    renderIcon: (props) => TEST_SET_TABS.TEST_RUN.renderIcon(props),
    children: (props) => <TestSetRunTestForm {...props} />,
    editOnly: true
  },
  TEST_SET_RESULTS_TAB: {
    key: TEST_SET_TABS.TEST_RESULTS.key,
    label: TEST_SET_TABS.TEST_RESULTS.label,
    renderIcon: (props) => TEST_SET_TABS.TEST_RESULTS.renderIcon(props),
    children: (props) => <TestSetResultsForm {...props} />,
    editOnly: true
  }

};

const TestSetModal = ({
  bodyClassName,
  footerClassName,
  onCancel = () => {},
  isCreateMode = false,
  defaultActiveTab = TEST_SET_TABS.TEST_SET.key,
  activeTestSetId = null,
  ...restProps
}) => {
  const { orgId, tenantId } = useParams();
  const userPermissions = useUserPermissions(orgId, tenantId);
  const canEdit = userPermissions.some(permission => PERMISSIONS_GROUP_EDITOR.includes(permission));
  const location = useLocation();
  const { navigateToTestSet } = useTestSetNavigation();
  const searchParams = new URLSearchParams(location.search);

  const [isLoading, setIsLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(defaultActiveTab);
  const [currentRef, setCurrentRef] = useState(null);

  const onTabChange = (tab) => {
    setCurrentTab(tab);
    navigateToTestSet(activeTestSetId, tab, null, false);
  };

  useEffect(() => {
    if(searchParams.get(TEST_SETS_SEARCH_PARAMS.ACTIVE_TAB) && searchParams?.get(TEST_SETS_SEARCH_PARAMS.ACTIVE_TAB) !== currentTab) {
      onTabChange(searchParams.get(TEST_SETS_SEARCH_PARAMS.ACTIVE_TAB));
    }
  }, [location.search]);

  const memoizedTabItems = useMemo(() => {

    return Object.values(TEST_SET_ACTIVE_TABS)
      .filter(tab =>
        !isCreateMode
          ? true
          : !tab.editOnly
      )
      .map(tab => ({
        label: (
          <Fragment>
            {tab.renderIcon()} {tab.label}
          </Fragment>
        ),
        key: tab.key,
        children: tab.key === currentTab
          ? tab?.children({
            ref: setCurrentRef,
            activeTestSetId,
            setIsLoading,
            isCreateMode
          })
          : null,
        disabled: isLoading
      }));
  }, [currentTab, activeTestSetId, isLoading]);

  return (
    <Fragment>
      <div className={bodyClassName}>
        <Tabs
          activeKey={currentTab}
          onChange={onTabChange}
          items={memoizedTabItems}
        />
      </div>

      <div className={footerClassName}>
        <Permissioned
          allowedPermissions={PERMISSIONS_GROUP_EDITOR}
          currentPermissions={userPermissions}
          fallbackComponent={null}
        >
          {currentRef?.renderFooter?.({ disabled: !canEdit })}
        </Permissioned>
      </div>
    </Fragment>
  );
};

TestSetModal.propTypes = {
  bodyClassName: PropTypes.string,
  footerClassName: PropTypes.string,
  onCancel: PropTypes.func,
  defaultActiveTab: PropTypes.string,
  isCreateMode: PropTypes.bool,
  activeTestSetId: PropTypes.string
};

export default TestSetModal;
